// S7 — SYNC CONFLICT REVIEW (spec 04 §4 S7, §5 row 25, §2.17 — slice 04-B-3).
//
// The office half of the outbox's honesty: every floor event the server could
// not apply (rejection — the payload preserved here is the ONLY copy of that
// floor truth) and every applied-but-flagged one (review_flag) lands in this
// queue, and NOTHING leaves it silently:
//
//   · a REJECTION resolves `apply_with_edit` (the corrected payload is
//     composed as a NEW event through the same apply pipeline — the original
//     is preserved, projections are never edited in place) or `discard`
//     (NOTE REQUIRED — discarding floor truth without a stated reason is the
//     silent overwrite §2.17 exists to prevent);
//   · a REVIEW FLAG resolves `acknowledge` (the applied numbers stand) or
//     `apply_with_edit` — it NEVER offers discard, because its event was
//     APPLIED: there is a projection standing on it, and "discard" would be a
//     lie about what the ledger already says.
//
// Roles: facility_admin (+ group_admin) via `production.conflict.resolve`.
// Redaction: n/a — no money rides an event (§7).
//
// THE REFUSAL'S FACTS ARE WORDED HERE, NEVER RELAYED (D-308 round 2, D-299
// round 9). A rejection's `event_payload` carries the server's English
// `server_note` and, when the refusal has a way on, a key this screen words in
// the reader's language: `way_on: inventory_stock_adjustment` on a floor
// return that synced after its order closed (discard it, then record the
// material with a stock adjustment in Inventory), and `awaiting_conflict_id`
// on a `downtime_ended` whose start is itself an open rejection (resolve the
// start first; the row links to it when it is on this page). A refused
// apply-with-edit carries the same keys on its 409, beside `reason`, and
// `mismatched_fields` when an event is already stored under the correction's
// key. A second resolver hears 409 FLOOR_CONFLICT_ALREADY_RESOLVED: the queue
// refetches (the hook) and a notice at screen level says so, because the
// resolved row leaves the open list with the refetch and would take a row
// sentence with it.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { joinSentences } from "../../../../lib/sentences";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  correctionEffectOf,
  useFloorConflicts,
  useResolveConflict,
  type FloorConflictResolveRequest,
  type FloorConflictsQuery,
  type FloorCorrectionEffect,
  type FloorSyncConflict,
} from "../hooks/production-queries";
import { eventKindKeyOf } from "../lib/event-kind";
import {
  formatQty,
  formatTimestamp,
  problemStringOf,
  productionErrorMessage,
  WAY_ON_STOCK_ADJUSTMENT,
  wayOnAdjustSearch,
} from "../lib/format";
import { AdjustStockLink } from "../../inventory/components/adjust-stock-link";
import { unitRefOfScanCode } from "../../inventory/lib/adjust";
import { parseMeasure } from "../../inventory/lib/measure";
import { enumLabel } from "../../../../lib/enum-label";

type StatusFilter = "open" | "resolved" | "all";

function queryOf(status: StatusFilter, kind: string, cursor: string | undefined) {
  const query: FloorConflictsQuery = {};
  if (status === "open") query.status = ["open"];
  if (status === "resolved") query.status = ["applied_with_edit", "discarded", "acknowledged"];
  if (kind === "rejection" || kind === "review_flag") query.kind = kind;
  if (cursor !== undefined) query.cursor = cursor;
  return query;
}

export function ConflictsScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>("open");
  const [kind, setKind] = useState<string>("all");
  // Cursor STACK: `cursors[cursors.length - 1]` is the current page; Next
  // pushes the served next_cursor, Back pops — the S1 pagination shape.
  const [cursors, setCursors] = useState<readonly string[]>([]);
  const cursor = cursors[cursors.length - 1];

  const query = useMemo(() => queryOf(status, kind, cursor), [status, kind, cursor]);
  const list = useFloorConflicts(query);
  /** The last correction that landed on a COMPLETED order (D-299 owner ruling
   * R1) — held HERE, not in the row's cell, because the resolved row leaves
   * the open list on the refetch and would take the notice with it. */
  const [receiptNotice, setReceiptNotice] = useState<FloorCorrectionEffect | undefined>(undefined);
  /** A resolution another reader committed first (409
   * FLOOR_CONFLICT_ALREADY_RESOLVED, D-299 round 9). Held at screen level for
   * the R1 notice's reason: the refetch takes the row out of the open list. */
  const [alreadyResolved, setAlreadyResolved] = useState(false);

  useEffect(() => {
    if (list.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [list.error, navigate]);

  const rows = list.data?.items ?? [];
  const idsOnPage = new Set(rows.map((row) => row.id));

  const columns: DataTableColumn<FloorSyncConflict>[] = [
    {
      key: "created_at",
      header: t("production.conflicts.col.when"),
      id: true,
      // The app's ONE timestamp formatter (F9). A bare `toLocaleString()`
      // follows the BROWSER's locale, so this column rendered US month-first
      // 12-hour times next to Russian dates elsewhere on the same screen.
      render: (row) => formatTimestamp(row.created_at, i18n.language),
    },
    {
      key: "reason",
      header: t("production.conflicts.col.reason"),
      render: (row) => (
        <span className="conflict-reason" data-reason={row.reason}>
          {enumLabel(t, "production.conflicts.reason", row.reason)}
        </span>
      ),
    },
    {
      key: "kind",
      header: t("production.conflicts.col.kind"),
      render: (row) => t(`production.conflicts.kind.${row.kind}`),
    },
    {
      key: "event_payload",
      header: t("production.conflicts.col.event"),
      render: (row) => (
        <>
          <div>{eventKindLabel(t, row)}</div>
          <ConflictFacts row={row} startOnPage={idsOnPage} />
          <details className="conflict-payload">
            <summary>{t("production.conflicts.payload")}</summary>
            <pre>{JSON.stringify(row.event_payload, null, 2)}</pre>
          </details>
        </>
      ),
    },
    {
      key: "work_order_id",
      header: term("work_order"),
      render: (row) =>
        row.work_order_id !== null ? (
          <Link to="/production/work-orders/$id" params={{ id: row.work_order_id }}>
            {term("work_order")}
          </Link>
        ) : (
          t("production.common.notAvailable")
        ),
    },
    {
      key: "status",
      header: t("production.conflicts.col.status"),
      render: (row) => enumLabel(t, "production.conflicts.rowStatus", row.status),
    },
    {
      key: "actions",
      header: t("production.common.actions"),
      actions: true,
      // A COMPONENT, not inline JSX: the resolve form holds per-row state
      // (mode, note, the edited payload) and state needs hooks, which a column
      // render function is not allowed to call. The form stays INSIDE the row's
      // own cell — M04-S09 addresses it as `row.getByTestId(…)`.
      render: (row) => (
        <ConflictResolveCell
          row={row}
          startOnPage={idsOnPage}
          onResolved={(response) => {
            // Replaced by the NEXT correction's notice, never cleared by an
            // unrelated resolution: the office works a queue, and a discard
            // on the row below must not wipe the one sentence that says stock
            // did not follow the correction above it. Close dismisses it.
            const effect = correctionEffectOf(response);
            if (effect !== undefined) setReceiptNotice(effect);
          }}
          onAlreadyResolved={() => {
            setAlreadyResolved(true);
          }}
        />
      ),
    },
  ];

  return (
    <section className="conflicts-list" data-testid="conflicts-screen">
      <PageHeader title={t("production.conflicts.title")} />
      {/* §4 house rule: resolve actions live in .wo-transitions, which the
          mobile-collapse block removes below md; this hint replaces them. */}
      <p className="production-mobile-hint">{t("production.conflicts.mobileHint")}</p>

      {/* D-299 OWNER RULING R1. A review flag's correction may land after the
          order completed (ruling A), and it restates the STEP — its quantity,
          scoreboard cell, piece rate and waste — and not what the completion
          already received into stock. Without this notice the office would
          read "applied" and believe stock followed; it did not, and only a
          person can decide the module-06 stock adjustment that reconciles it. */}
      {receiptNotice !== undefined ? (
        <ReceiptUnchangedNotice
          effect={receiptNotice}
          onDismiss={() => {
            setReceiptNotice(undefined);
          }}
        />
      ) : null}

      {alreadyResolved ? (
        <Banner
          kind="warn"
          className="conflict-already-resolved"
          data-testid="conflict-already-resolved"
          onDismiss={() => {
            setAlreadyResolved(false);
          }}
          dismissLabel={t("production.common.close")}
        >
          {t("production.conflicts.alreadyResolved")}
        </Banner>
      ) : null}

      <div className="filter-bar">
        <label className="filter-chip">
          <span className="filter-chip-label">{t("production.conflicts.filter.status")}</span>
          <select
            value={status}
            data-testid="conflicts-status-filter"
            onChange={(event) => {
              setStatus(event.target.value as StatusFilter);
              setCursors([]);
            }}
          >
            <option value="open">{t("production.conflicts.status.open")}</option>
            <option value="resolved">{t("production.conflicts.filter.resolved")}</option>
            <option value="all">{t("production.conflicts.filter.all")}</option>
          </select>
        </label>
        <label className="filter-chip">
          <span className="filter-chip-label">{t("production.conflicts.filter.kind")}</span>
          <select
            value={kind}
            data-testid="conflicts-kind-filter"
            onChange={(event) => {
              setKind(event.target.value);
              setCursors([]);
            }}
          >
            <option value="all">{t("production.conflicts.filter.all")}</option>
            <option value="rejection">{t("production.conflicts.kind.rejection")}</option>
            <option value="review_flag">{t("production.conflicts.kind.review_flag")}</option>
          </select>
        </label>
      </div>

      {list.isPending ? (
        <TableSkeleton rows={6} cols={6} label={t("production.common.loading")} />
      ) : null}

      {list.error && !(list.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("production.conflicts.error")}
          detail={list.error.message}
          retryLabel={t("production.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("production.common.requestId")}
        />
      ) : null}

      {list.data !== undefined && rows.length === 0 ? (
        <EmptyState
          title={
            status === "open"
              ? t("production.conflicts.emptyOpenTitle")
              : t("production.conflicts.emptyTitle")
          }
          body={
            status === "open"
              ? t("production.conflicts.emptyOpenBody")
              : t("production.conflicts.emptyBody")
          }
        />
      ) : null}

      {rows.length > 0 ? (
        <DataTable
          data-testid="conflicts-table"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          empty={null}
          rowTestId="conflict-row"
          // `id` + `tabIndex` let an End that waits on its start move the
          // reader to the start's row (ConflictFacts, D-308 round 2).
          rowProps={(row) => ({
            "data-kind": row.kind,
            "data-conflict-id": row.id,
            id: conflictRowId(row.id),
            tabIndex: -1,
          })}
        />
      ) : null}

      <div className="production-pager">
        {cursors.length > 0 ? (
          <Button
            onClick={() => {
              setCursors(cursors.slice(0, -1));
            }}
          >
            {t("production.conflicts.prevPage")}
          </Button>
        ) : null}
        {(() => {
          const next = list.data?.next_cursor ?? null;
          return next !== null ? (
            <Button
              data-testid="conflicts-next-page"
              onClick={() => {
                setCursors([...cursors, next]);
              }}
            >
              {t("production.conflicts.nextPage")}
            </Button>
          ) : null;
        })()}
      </div>
    </section>
  );
}

type ResolveMode = "apply_with_edit" | "discard" | "acknowledge";

/** The floor envelope's own event name, or `undefined` when the preserved
 * payload does not carry one (a malformed envelope is still a fact worth
 * queueing — the column then says "not available" rather than blanking). */
function eventTypeOf(row: FloorSyncConflict): string | undefined {
  const envelope = row.event_payload as { event_type?: string };
  return typeof envelope.event_type === "string" ? envelope.event_type : undefined;
}

/** The envelope's kind, worded. A NEGATIVE material_issued is the floor
 * RETURN riding the same §5.1 row (D-081) — worded as what it physically was,
 * exactly as the Events tab words it. A kind this build does not know renders
 * its raw name: a fact rendered oddly beats a fact hidden. */
function eventKindLabel(t: TFunction, row: FloorSyncConflict): string {
  const kind = eventTypeOf(row);
  if (kind === undefined) return t("production.common.notAvailable");
  if (isReturnRow(row)) return t("production.detail.events.kind.material_returned");
  const key = eventKindKeyOf(kind);
  return key === undefined ? kind : t(key);
}

/** A NEGATIVE `material_issued`: the floor RETURN riding the same §5.1 row
 * (D-081). */
function isReturnRow(row: FloorSyncConflict): boolean {
  if (eventTypeOf(row) !== "material_issued") return false;
  const envelope = row.event_payload as { payload?: Record<string, unknown> };
  const qty = envelope.payload?.["qty"];
  return typeof qty === "string" && qty.startsWith("-");
}

/**
 * The spec 06 §4.12 search for a floor RETURN refused because its order closed
 * (D-315): the component when the payload names one, the CONTAINER when
 * the worker scanned one (its label URL carries the id, a typed serial the
 * serial; the form resolves it and follows D-308 round 2, filling the product
 * and batch from it), and the returned quantity as a hint, never typed in for
 * the person — booked back out of work in progress. The floor payload carries
 * no batch and no plant: the form takes the plant from the order.
 */
function returnAdjustSearch(row: FloorSyncConflict) {
  const envelope = row.event_payload as { payload?: Record<string, unknown> };
  const payload = envelope.payload ?? {};
  const product = payload["component_product_id"];
  const scan = payload["scan_code"];
  const qty = payload["qty"];
  const uom = payload["uom"];
  const container = typeof scan === "string" ? unitRefOfScanCode(scan) : {};
  return wayOnAdjustSearch({
    workOrderId: row.work_order_id,
    ...(typeof product === "string" && product.length > 0 ? { productId: product } : {}),
    ...("unit_id" in container ? { unitId: container.unit_id } : {}),
    ...("unit_serial" in container ? { unitSerial: container.unit_serial } : {}),
    ...(typeof qty === "string" ? { expectedQty: qty } : {}),
    ...(typeof uom === "string" ? { uom } : {}),
    direction: "increase",
    reason: "found",
    from: "conflict",
  });
}

/** The DOM id of a conflict's row, so another row can move the reader to it. */
function conflictRowId(id: string): string {
  return `conflict-${id}`;
}

/** A string fact the server stored beside the rejection's note (§2.17), or
 * `undefined`. */
function payloadFactOf(row: FloorSyncConflict, key: string): string | undefined {
  const value = (row.event_payload as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** Scroll to a conflict's row and give it focus. Returns false when the row is
 * not rendered (another page, or filtered out). */
function showConflictRow(id: string): boolean {
  const el = document.getElementById(conflictRowId(id));
  if (el === null) return false;
  // jsdom has no scrollIntoView; a browser always does.
  if ("scrollIntoView" in el) el.scrollIntoView({ block: "center" });
  el.focus();
  return true;
}

/** The control that moves the reader to the start an End waits on, or a
 * sentence saying the start is not on this page. */
function AwaitedStartLink({
  startId,
  onPage,
  testId,
}: {
  startId: string;
  onPage: ReadonlySet<string>;
  testId: string;
}) {
  const { t } = useTranslation();
  return onPage.has(startId) ? (
    <button
      type="button"
      className="ds-lnk"
      data-testid={testId}
      data-conflict-id={startId}
      onClick={() => {
        showConflictRow(startId);
      }}
    >
      {t("production.conflicts.awaiting.showStart")}
    </button>
  ) : (
    <span data-testid={`${testId}-elsewhere`}>
      {t("production.conflicts.awaiting.startElsewhere")}
    </span>
  );
}

/**
 * THE REFUSAL'S FACTS, IN THE READER'S LANGUAGE (D-308 round 2, §2.17). The
 * server stores two ways on as keys beside its English note, and each gets a
 * sentence on an OPEN row: a floor return refused because its order closed
 * (`way_on`), and an End refused because its start is itself open in this
 * queue (`awaiting_conflict_id`). A resolved row is history and says nothing
 * more. A value this build does not know renders nothing — the raw payload
 * below still carries it.
 */
function ConflictFacts({
  row,
  startOnPage,
}: {
  row: FloorSyncConflict;
  startOnPage: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  if (row.status !== "open") return null;
  const wayOn = payloadFactOf(row, "way_on");
  const awaiting = payloadFactOf(row, "awaiting_conflict_id");
  return (
    <>
      {wayOn === WAY_ON_STOCK_ADJUSTMENT ? (
        <p
          className="production-note conflict-fact"
          role="note"
          data-testid="conflict-way-on"
          data-way-on={wayOn}
        >
          {t("production.conflicts.wayOn.returnAfterClose")}{" "}
          <AdjustStockLink
            search={returnAdjustSearch(row)}
            label={t("inventory.adjust.fromProduction")}
            testId="conflict-way-on-adjust"
          />
        </p>
      ) : null}
      {awaiting !== undefined ? (
        <p
          className="production-note conflict-fact"
          role="note"
          data-testid="conflict-awaiting"
          data-awaiting-conflict-id={awaiting}
        >
          {t("production.conflicts.awaiting.note")}{" "}
          <AwaitedStartLink
            startId={awaiting}
            onPage={startOnPage}
            testId="conflict-awaiting-start"
          />
        </p>
      ) : null}
    </>
  );
}

/** The words for e25's `mismatched_fields` (D-299 round 9): which of the four
 * envelope fields (`event_type`, `worker_id`, `work_center_id`,
 * `wo_operation_id`) an event already stored under the correction's key
 * differs in. A field this build has no word for reads as the reader's
 * "Other" (`enumLabel`, D-441): the sentence still says something else
 * differs, and no wire name such as `shift_id` reaches the glass. */
function mismatchedFieldsText(
  t: TFunction,
  language: string,
  error: RequestError,
): string | undefined {
  const raw = (error.problem as Record<string, unknown> | undefined)?.["mismatched_fields"];
  if (!Array.isArray(raw)) return undefined;
  const words = raw
    .filter((field): field is string => typeof field === "string" && field.length > 0)
    .map((field) => enumLabel(t, "production.conflicts.mismatchField", field));
  if (words.length === 0) return undefined;
  return new Intl.ListFormat(language, { type: "conjunction" }).format(words);
}

/** S7's R1 notice (D-299): the correction applied, the receipt did not move.
 * Both figures ride the response, so it is said in the reader's language
 * without a re-read, and the one remedy — a stock adjustment — is named. The
 * client never judges WHICH step fed the receipt: since round 4 the server
 * sends `correction_effect` only for the step the completion took its output
 * from, and null for any other, so an earlier step's correction shows no
 * notice and the notice's "stock may now be wrong" is true when it shows. */
function ReceiptUnchangedNotice({
  effect,
  onDismiss,
}: {
  effect: FloorCorrectionEffect;
  onDismiss: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qty = (value: string): string => formatQty(value, { locale: i18n.language });
  // D-315: the remedy links to spec 06 §4.12 on the order's finished
  // output. The receipt took MORE than the corrected step made → take the
  // difference off stock back into work in progress; LESS → add it. No
  // quantity is carried: the step's unit may not be the output's.
  const output =
    effect.receipted_outputs.find((o) => o.kind === "finished") ?? effect.receipted_outputs[0];
  const corrected = parseMeasure(effect.corrected_operation_qty_good);
  const receipted = parseMeasure(effect.receipted_qty_good);
  const direction =
    corrected !== undefined && receipted !== undefined && corrected < receipted
      ? "decrease"
      : "increase";
  return (
    <Banner
      kind="warn"
      className="conflict-receipt-unchanged"
      data-testid="conflict-receipt-unchanged"
      onDismiss={onDismiss}
      dismissLabel={t("production.common.close")}
    >
      <p>
        {t("production.conflicts.receiptUnchanged.corrected", {
          good: qty(effect.corrected_operation_qty_good),
          scrap: qty(effect.corrected_operation_qty_scrap),
          uom: effect.uom,
        })}
      </p>
      <p>
        {t("production.conflicts.receiptUnchanged.receipt", {
          receipted: qty(effect.receipted_qty_good),
          uom: effect.uom,
        })}
      </p>
      <p>
        {t("production.conflicts.receiptUnchanged.remedy")}{" "}
        <Link
          to="/production/work-orders/$id"
          params={{ id: effect.work_order_id }}
          className="ds-lnk"
          data-testid="conflict-receipt-unchanged-wo"
        >
          {t("production.conflicts.receiptUnchanged.openOrder")}
        </Link>{" "}
        <AdjustStockLink
          search={wayOnAdjustSearch({
            workOrderId: effect.work_order_id,
            ...(output !== undefined ? { productId: output.product_id } : {}),
            direction,
            reason: "data_correction",
            from: "conflict",
          })}
          label={t("inventory.adjust.fromProduction")}
          testId="conflict-receipt-unchanged-adjust"
        />
      </p>
    </Banner>
  );
}

/**
 * The sentence for a refused e25 resolution.
 *
 * `FLOOR_CONFLICT_RESOLUTION_REJECTED` is worded HERE, in the reader's
 * language, and never relayed (D-299 round-3 refutation): its title is
 * English ("The corrected event no longer applies") and it names no exit,
 * while R6 relies on the reader discarding the row — an office apply-with-edit
 * of an old rejected End whose stoppage has since ended answers exactly this,
 * and the conflict stays open for a discard. The exit depends on the row's
 * kind, because §2.17 offers a rejection `discard` and a review flag
 * `acknowledge`, never the other. `DOWNTIME_END_AT_INVALID` (Q2) and
 * `DOWNTIME_START_AT_INVALID` (S1, U4) and `LABOR_AT_INVALID` (U1) are worded here too, and a
 * refused re-applied Complete names S2's record-result form (V4). Every other code keeps the pre-existing path: the server's title,
 * else the generic "refused".
 *
 * Since D-299 round 9 and D-308 round 2: `FLOOR_CONFLICT_ALREADY_RESOLVED`
 * has its own sentence, and a `FLOOR_CONFLICT_RESOLUTION_REJECTED` carrying
 * `way_on`, `awaiting_conflict_id` or `mismatched_fields` names that fact's
 * way on (the mismatched fields in words) before the kind's exit is used.
 *
 * Since the D-308 review: a re-applied RETURN refused by module 06's 422
 * `UNIT_NOT_AVAILABLE` (a held container, or one a concurrent draw emptied)
 * has its own sentence, and the last fallback is the area's own mapping
 * (`productionErrorMessage`: a translated code, then the title, then the
 * generic "refused"), so no code this area can word reaches the reader as the
 * server's English title.
 */
function resolveRefusalMessage(
  t: TFunction,
  language: string,
  error: RequestError,
  kind: FloorSyncConflict["kind"],
  eventType: string | undefined,
  isReturn: boolean,
): string {
  // D-299 round 9: a second resolver. The first resolution committed while
  // this reader held the row, so nothing they entered applied. Our sentence,
  // never the server's English title; the queue refetches (the hook).
  if (error.code === "FLOOR_CONFLICT_ALREADY_RESOLVED") {
    return t("production.conflicts.alreadyResolved");
  }
  if (error.code === "FLOOR_CONFLICT_RESOLUTION_REJECTED") {
    // The refusal's facts first (D-308 round 2, D-299 round 9): each names
    // its own way on. Without one, the kind's exit (round 4).
    if (problemStringOf(error, "way_on") === WAY_ON_STOCK_ADJUSTMENT) {
      return t("production.conflicts.wayOn.rejectedAfterClose");
    }
    if (problemStringOf(error, "awaiting_conflict_id") !== undefined) {
      return t("production.conflicts.awaiting.rejected");
    }
    const fields = mismatchedFieldsText(t, language, error);
    if (fields !== undefined) {
      return t(
        kind === "review_flag"
          ? "production.conflicts.correctionMismatch.review_flag"
          : "production.conflicts.correctionMismatch.rejection",
        { fields },
      );
    }
    return t(
      kind === "review_flag"
        ? "production.conflicts.resolutionRejected.review_flag"
        : "production.conflicts.resolutionRejected.rejection",
    );
  }
  // D-299 owner ruling Q2: the trusted re-apply of a rejected End takes e35's
  // own 24-hour bound, and its refusal is e35's own 422. The corrected payload
  // cannot move the instant (it rides the envelope), so the sentence names the
  // way on — discard here, end it on the blocked-stations board — rather than
  // relaying e35's English title, which speaks of a "stated end" the S7
  // reader never stated.
  // D-299 owner ruling V4: a refused re-applied Complete leaves the step NOT
  // complete — paused when the tablet had paused it — and discarding the row
  // does not complete it either. The only office act that does is S2's
  // record-result form (e34), so this sentence names it, with the form's own
  // labels read from its keys so the two cannot drift. The step's quantity is
  // already recorded (the floor's §3.2 guard ran before the refusal), and e34
  // completes such a step with no new quantity (owner ruling W1), so the
  // sentence says none is needed. Its window code is
  // DOWNTIME_END_AT_INVALID when the Complete would end the pause's stoppage
  // (e34 then ends that stoppage too), LABOR_AT_INVALID otherwise.
  if (eventType === "op_completed") {
    const labels = {
      record: t("production.detail.ops.record"),
      tab: t("production.detail.tab.operations"),
      complete: t("production.detail.ops.complete"),
    };
    const bound = (error.problem as Record<string, unknown> | undefined)?.["bound"];
    // Each part is its own short sentence (D-425): what happened and the way
    // on, then that no quantity is needed, then the stoppage's two ways on.
    if (error.code === "DOWNTIME_END_AT_INVALID" && bound === "before_window") {
      return joinSentences(
        [
          t("production.conflicts.completeStoppageBeforeWindow", labels),
          t("production.conflicts.noNewQuantity"),
          t("production.conflicts.completeStoppageWayOn"),
        ],
        language,
      );
    }
    if (error.code === "LABOR_AT_INVALID") {
      return joinSentences(
        [
          t("production.conflicts.completeLaborBeforeWindow", labels),
          t("production.conflicts.noNewQuantity"),
        ],
        language,
      );
    }
  }
  if (error.code === "DOWNTIME_END_AT_INVALID") {
    const bound = (error.problem as Record<string, unknown> | undefined)?.["bound"];
    return t(
      bound === "before_window"
        ? "production.conflicts.endBeforeWindow"
        : "production.conflicts.endAtInvalid",
    );
  }
  // D-299 owner ruling S1: a re-applied tap that STARTS a stoppage (a
  // `downtime_started`, or an `op_paused` carrying a downtime reason) takes
  // the same 24-hour bound, refused with the start's own code. No office act
  // opens a stoppage, so the only way on is the discard; the end's sentence
  // would tell the reader the tablet ENDED something and send them to a board
  // that cannot help. Its one bound today is `before_window`.
  // D-299 owner rulings U4 and U1: a refused `op_paused` gets its own
  // sentence, because its corrected payload visibly carries the downtime
  // reason and a reader will try editing it out. Under U1 that edit is
  // refused too (the pause still stops labor at the same instant), so the
  // sentence says so and names the discard, as the server's detail does.
  if (error.code === "DOWNTIME_START_AT_INVALID") {
    return t(
      eventType === "op_paused"
        ? "production.conflicts.pauseStartBeforeWindow"
        : "production.conflicts.startBeforeWindow",
    );
  }
  // D-299 owner ruling U1: a re-applied labor tap (Start, pause, resume,
  // Complete) that opens or ends no stoppage, more than 24 hours back. Its one
  // bound is `before_window`, and the discard is the way on.
  if (error.code === "LABOR_AT_INVALID") {
    return t("production.conflicts.laborBeforeWindow");
  }
  if (error.code === "UNIT_NOT_AVAILABLE" && isReturn) {
    return t("production.conflicts.returnUnitNotAvailable");
  }
  // A failure with no problem body (a gateway's page) carries only the
  // client's own label as its message, never a sentence for the reader.
  if (error.problem === undefined) return t("production.conflicts.resolveFailed");
  return productionErrorMessage(t, error, "production.conflicts.resolveFailed");
}

/** The Actions cell: the three §2.17 resolutions and the form one of them
 * opens. Its own component because the form is stateful. `onResolved` hands
 * the e25 response up to the screen, which outlives this row. */
function ConflictResolveCell({
  row,
  startOnPage,
  onResolved,
  onAlreadyResolved,
}: {
  row: FloorSyncConflict;
  startOnPage: ReadonlySet<string>;
  onResolved: (response: unknown) => void;
  /** e25 answered 409 FLOOR_CONFLICT_ALREADY_RESOLVED (D-299 round 9). */
  onAlreadyResolved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const resolve = useResolveConflict();
  const [mode, setMode] = useState<ResolveMode | undefined>(undefined);
  const [note, setNote] = useState("");
  const envelope = row.event_payload as {
    event_type?: string;
    worker_id?: string;
    occurred_at?: string;
    payload?: Record<string, unknown>;
  };
  const [corrected, setCorrected] = useState(() => JSON.stringify(envelope.payload ?? {}, null, 2));
  const [jsonError, setJsonError] = useState(false);

  const isOpen = row.status === "open";
  const isRejection = row.kind === "rejection";

  const submit = (): void => {
    if (mode === undefined) return;
    if (mode === "discard" && note.trim().length === 0) return;
    const body: FloorConflictResolveRequest = { action: mode };
    if (note.trim().length > 0) body.note = note.trim();
    if (mode === "apply_with_edit") {
      try {
        body.corrected_payload = JSON.parse(corrected) as Record<string, unknown>;
        setJsonError(false);
      } catch {
        setJsonError(true);
        return;
      }
    }
    resolve.mutate(
      { id: row.id, body },
      {
        onSuccess: (response) => {
          setMode(undefined);
          setNote("");
          onResolved(response);
        },
        onError: (error) => {
          // Someone else resolved it first. The queue refetches (the hook) and
          // this row leaves the open list, so the screen says it, not the row,
          // and the form closes rather than repeat the notice under a filter
          // that keeps resolved rows.
          if (error instanceof RequestError && error.code === "FLOOR_CONFLICT_ALREADY_RESOLVED") {
            setMode(undefined);
            onAlreadyResolved();
          }
        },
      },
    );
  };

  const resolveError = resolve.error instanceof RequestError ? resolve.error : undefined;
  // An End re-applied while its start is still open (D-308 round 2): the
  // refusal names the start's conflict, and the sentence links to its row.
  const awaitedStart =
    resolveError?.code === "FLOOR_CONFLICT_RESOLUTION_REJECTED"
      ? problemStringOf(resolveError, "awaiting_conflict_id")
      : undefined;

  return (
    <>
      {isOpen ? (
        <div className="wo-transitions conflict-actions">
          <Button
            data-testid="conflict-apply-edit"
            onClick={() => {
              setMode("apply_with_edit");
            }}
          >
            {t("production.conflicts.applyWithEdit")}
          </Button>
          {isRejection ? (
            <Button
              data-testid="conflict-discard"
              onClick={() => {
                setMode("discard");
              }}
            >
              {t("production.conflicts.discard")}
            </Button>
          ) : (
            <Button
              data-testid="conflict-acknowledge"
              onClick={() => {
                setMode("acknowledge");
              }}
            >
              {t("production.conflicts.acknowledge")}
            </Button>
          )}
        </div>
      ) : null}

      {mode !== undefined ? (
        <div className="conflict-resolve-form" data-testid="conflict-resolve-form">
          {mode === "apply_with_edit" ? (
            <>
              {/* §3.10 Field: same label-wraps-control association the bare
                  <label> had; the testid stays on the textarea. */}
              <Field label={t("production.conflicts.correctedPayload")}>
                <textarea
                  value={corrected}
                  rows={6}
                  data-testid="conflict-corrected-payload"
                  onChange={(event) => {
                    setCorrected(event.target.value);
                  }}
                />
              </Field>
              {jsonError ? (
                <p className="conflict-form-error" role="alert">
                  {t("production.conflicts.badJson")}
                </p>
              ) : null}
            </>
          ) : null}
          {mode === "discard" ? (
            <p className="conflict-form-warning" data-testid="conflict-discard-warning">
              {t("production.conflicts.discardWarning")}
            </p>
          ) : null}
          <Field
            label={
              mode === "discard"
                ? t("production.conflicts.noteRequired")
                : t("production.conflicts.note")
            }
          >
            <textarea
              value={note}
              rows={2}
              data-testid="conflict-note"
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>
          <div className="conflict-form-actions">
            <Button
              data-testid="conflict-resolve-submit"
              busy={resolve.isPending}
              busyLabel={t("production.common.saving")}
              disabled={mode === "discard" && note.trim().length === 0}
              onClick={submit}
            >
              {t("production.conflicts.confirmResolve")}
            </Button>
            <Button
              onClick={() => {
                setMode(undefined);
                resolve.reset();
              }}
            >
              {t("production.common.cancel")}
            </Button>
          </div>
          {resolveError !== undefined ? (
            <p
              className="conflict-form-error"
              role="alert"
              data-testid="conflict-resolve-error"
              data-code={resolveError.code}
            >
              {resolveRefusalMessage(
                t,
                i18n.language,
                resolveError,
                row.kind,
                eventTypeOf(row),
                isReturnRow(row),
              )}
              {awaitedStart !== undefined ? (
                <>
                  {" "}
                  <AwaitedStartLink
                    startId={awaitedStart}
                    onPage={startOnPage}
                    testId="conflict-resolve-error-start"
                  />
                </>
              ) : null}
              {resolveError.code === "FLOOR_CONFLICT_RESOLUTION_REJECTED" &&
              problemStringOf(resolveError, "way_on") === WAY_ON_STOCK_ADJUSTMENT ? (
                <>
                  {" "}
                  <AdjustStockLink
                    search={returnAdjustSearch(row)}
                    label={t("inventory.adjust.fromProduction")}
                    testId="conflict-resolve-error-adjust"
                  />
                </>
              ) : null}
              {resolveError.requestId !== undefined ? (
                <>
                  {" "}
                  <code>{resolveError.requestId}</code>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
